export interface ArticleBlock {
  type: "h2" | "h3" | "p" | "quote" | "list";
  text?: string;
  items?: string[];
}

export interface Article {
  id: string;
  title: string;
  categorySlug: string;
  excerpt: string;
  cover: string;
  coverAlt: string;
  author: string;
  authorRole: string;
  date: string;
  readTime: string;
  views: number;
  comments: number;
  tags: string[];
  hot: boolean;
  blocks: ArticleBlock[];
}

export const articles: Article[] = [
  {
    id: "variety-name-scenes-weekly",
    title: "本周综艺名场面盘点：五个出圈瞬间为何在同一周刷屏",
    categorySlug: "variety",
    excerpt:
      "同一周里五个综艺片段先后冲上热搜，看似偶然，其实都踩中了同一个情绪开关。我们把它们放在一起看，会发现出圈有迹可循。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20empty%20variety%20show%20stage%20with%20glowing%20amber%20spotlights%20and%20blurred%20led%20screens%2C%20reflective%20dark%20floor%2C%20shallow%20depth%20of%20field%2C%20cinematic%20editorial%20entertainment%20photography%2C%20warm%20color%20grading%2C%20high%20detail&width=1200&height=800&seq=hlk-cover-variety-01&orientation=landscape",
    coverAlt: "综艺舞台灯光与屏幕背景的示意图",
    author: "编辑部 · 何一",
    authorRole: "综艺板块编辑",
    date: "2026-09-12",
    readTime: "7 分钟",
    views: 48210,
    comments: 326,
    tags: ["综艺名场面", "本周盘点", "出圈瞬间"],
    hot: true,
    blocks: [
      {
        type: "p",
        text: "这一周打开任意一个平台，几乎都会被同一批综艺片段反复推到眼前。它们来自不同的节目、不同的嘉宾、不同的赛制，却在两三天内先后完成出圈。把五个片段并排放在一起看，会发现问题并不在于节目做得多热闹，而在于它们都精准地踩中了同一种情绪。",
      },
      { type: "h2", text: "出圈片段的第一共性：有一个能被复述的瞬间" },
      {
        type: "p",
        text: "能传播的片段，往往可以用一句话讲清楚。它不需要完整的上下文，也不需要观众看过整期节目。观众把这句话截图发出去，对方立刻能会心一笑。这种「自解释」的能力，是片段能脱离节目独立传播的前提。",
      },
      {
        type: "quote",
        text: "真正出圈的不是节目本身，而是观众看完之后想转发给朋友的那一句话。",
      },
      { type: "h2", text: "五个片段回看：它们各自提供了什么" },
      {
        type: "list",
        items: [
          "反转型：嘉宾先给出一个看似严肃的判断，最后一秒自我推翻，反差本身构成笑点。",
          "共情型：一句看似平常的感慨，恰好说中了大量观众当下的处境，被大量引用。",
          "失控型：流程之外的临场反应，因为不可复制而被反复回看。",
          "群像型：几个嘉宾同时接住同一个梗，节奏密集，适合被剪成短视频。",
          "反差型：一贯稳重的嘉宾突然放飞，打破了观众对他的既有印象。",
        ],
      },
      { type: "h2", text: "为什么总是「同一周」集中出现" },
      {
        type: "p",
        text: "综艺的排播本身就有聚集效应。周中到周末的节目密度最高，观众的空闲时间也集中在晚上，于是多个节目的高光往往被压缩在同一段时间里互相争夺注意力。当其中一个片段先出圈，其余片段会被算法一并推荐，形成滚雪球式的效果。",
      },
      {
        type: "p",
        text: "对观众来说，这意味着一周里总会有两三天信息量过大。与其追着热搜一条条点开，不如在固定时间看一份整理好的清单，这也是我们每日更新「今日看点」的原因。",
      },
    ],
  },
  {
    id: "drama-september-watchlist",
    title: "九月追剧清单：六部待播剧的看点与选角思路",
    categorySlug: "drama",
    excerpt:
      "九月待播的六部剧类型跨度不小，从悬疑到年代，从职场到古装。我们按选角思路和叙事看点做了整理，帮你决定先追哪一部。",
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20moody%20film%20set%20corridor%20with%20warm%20practical%20lights%20and%20soft%20haze%2C%20empty%20frame%20suggesting%20a%20scene%2C%20editorial%20magazine%20photography%2C%20deep%20teal%20and%20amber%20palette%2C%20high%20detail%2C%20vertical%20leading%20lines&width=1200&height=800&seq=hlk-cover-drama-01&orientation=landscape",
    coverAlt: "影视拍摄现场走廊的示意图",
    author: "编辑部 · 沈迟",
    authorRole: "影视板块编辑",
    date: "2026-09-11",
    readTime: "8 分钟",
    views: 39640,
    comments: 241,
    tags: ["追剧清单", "待播剧", "选角"],
    hot: true,
    blocks: [
      {
        type: "p",
        text: "九月的剧集排播延续了暑期档的密集节奏，但类型构成明显更分散。悬疑、年代、职场、古装各自占位，观众的注意力也被拆得更细。与其盲目追，不如先看清楚每部剧真正想讲什么。",
      },
      { type: "h2", text: "选角思路决定了一部剧的气质" },
      {
        type: "p",
        text: "一部剧的气质，往往在选角阶段就已经确定。选择有生活质感的演员，故事会往写实方向走；选择自带喜剧节奏的演员，哪怕剧本是正剧，观众也会先入为主地寻找轻松的时刻。看选角阵容，其实是在提前预判这部剧的调性。",
      },
      { type: "h3", text: "值得留意的三种搭配" },
      {
        type: "list",
        items: [
          "老戏骨搭配新人：通常意味着剧方把重心放在群像与表演上，节奏会偏慢但更耐看。",
          "跨类型组合：把擅长喜剧的演员放进严肃题材，这类搭配要么惊喜要么割裂，风险与看点并存。",
          "熟脸重聚：演员之间的默契能快速建立观感，但也容易让观众停留在上一部作品的印象里。",
        ],
      },
      { type: "h2", text: "如何判断一部剧值不值得追" },
      {
        type: "p",
        text: "不必等到开分再决定。看前两集的叙事效率就能判断大半：如果两集之后主线仍未建立、人物动机仍靠台词解释，那大概率是后期才进入状态的类型，适合养肥了再看。",
      },
      {
        type: "quote",
        text: "追剧最贵的成本不是时间，而是被半途弃剧耽误的期待。",
      },
      {
        type: "p",
        text: "我们的做法是，在每部剧开播前整理一份「看点清单」，标注叙事节奏、主要看点与适合的观看方式，帮你在点开第一集之前就知道自己要花多少注意力。",
      },
    ],
  },
  {
    id: "internet-meme-spread-path",
    title: "从一句台词到全网二创：网络热梗的传播路径拆解",
    categorySlug: "internet",
    excerpt:
      "热梗看起来是突然冒出来的，其实每一次传播都遵循相似的路径。我们复盘了最近一个出圈梗的完整传播链条，看看它是怎么一步步扩散的。",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20digital%20network%20of%20glowing%20amber%20nodes%20and%20connecting%20lines%20on%20a%20dark%20warm%20charcoal%20background%2C%20data%20flow%20visualization%2C%20editorial%20technology%20magazine%20style%2C%20high%20detail%2C%20cinematic%20glow&width=1200&height=800&seq=hlk-cover-internet-01&orientation=landscape",
    coverAlt: "网络传播路径的可视化示意图",
    author: "编辑部 · 郑野",
    authorRole: "热点板块编辑",
    date: "2026-09-10",
    readTime: "6 分钟",
    views: 35870,
    comments: 189,
    tags: ["网络热梗", "二创", "传播路径"],
    hot: false,
    blocks: [
      {
        type: "p",
        text: "一个梗从出现到全网皆知，通常只需要两到三天。但如果把过程拆开来看，会发现它几乎总是沿着固定的几步推进。理解这个路径，也就理解了为什么有些内容能火，有些内容再用力也推不动。",
      },
      { type: "h2", text: "第一步：在原生语境里被记住" },
      {
        type: "p",
        text: "梗最初往往出现在某个具体场景里，比如一段节目、一句台词、一个表情。它必须先在自己的语境中被一部分人记住，才算具备传播的种子。此时它的受众还很窄，但记忆点足够强。",
      },
      { type: "h2", text: "第二步：被剪成可以脱离语境的形式" },
      {
        type: "p",
        text: "真正让梗扩散的，是有人把它从原语境里剪了出来，做成了十几秒的短视频。这一步的关键在于「去语境化」——即使观众没看过原片，也能看懂并使用。",
      },
      { type: "h2", text: "第三、四步：二创与日常化" },
      {
        type: "list",
        items: [
          "二创期：大量用户套用同一结构，换上自己的内容，梗变成了一种句式或模板。",
          "日常化：梗开始脱离原本的娱乐语境，被用进日常聊天与工作场景，此时它才真正进入大众语汇。",
        ],
      },
      {
        type: "quote",
        text: "一个梗真正活下来的标志，是人们在忘记它出处之后，依然在使用它。",
      },
      {
        type: "p",
        text: "所以当一个热点刚冒头时，我们会先判断它处在传播路径的第几步。处在第三步的梗往往还有扩散空间，而已经进入日常化的梗，热度反而开始回落。这个判断，也是我们每天筛选看点的一个参考维度。",
      },
    ],
  },
  {
    id: "stars-daily-content-battle",
    title: "舞台之外：艺人们如何把日常更新变成新的内容战场",
    categorySlug: "stars",
    excerpt:
      "过去艺人靠作品与通告维持存在感，如今日常更新本身就成了内容。我们观察了近期几位艺人的更新节奏，总结出三种不同的表达策略。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20backstage%20vanity%20area%20with%20soft%20mirror%20lights%20and%20a%20blurred%20silhouette%20in%20the%20distance%2C%20muted%20beige%20and%20amber%20tones%2C%20editorial%20fashion%20photography%2C%20shallow%20depth%20of%20field%2C%20high%20detail&width=1200&height=800&seq=hlk-cover-stars-01&orientation=landscape",
    coverAlt: "后台化妆间灯光的示意图",
    author: "编辑部 · 苏禾",
    authorRole: "人物板块编辑",
    date: "2026-09-09",
    readTime: "6 分钟",
    views: 27650,
    comments: 154,
    tags: ["明星动态", "日常更新", "内容策略"],
    hot: false,
    blocks: [
      {
        type: "p",
        text: "作品之间总有空窗期，而日常更新填满了这段空窗。对艺人来说，更新不再只是「营业」，而是一套需要经营的内容策略。不同的策略，也决定了观众最终记住的是作品，还是人设。",
      },
      { type: "h2", text: "三种常见的表达策略" },
      {
        type: "list",
        items: [
          "作品导向型：更新始终围绕正在进行的项目，日常内容像创作手记，观众记住的是角色与作品。",
          "生活分享型：以松弛的日常为主，强调真实感，容易积累亲近感，但也更容易被讨论私生活。",
          "互动玩梗型：主动接住网络热梗，节奏轻快，传播效率高，但需要把握好分寸，避免过度消耗。",
        ],
      },
      { type: "h2", text: "观众缘是怎么攒起来的" },
      {
        type: "p",
        text: "观众缘很少来自单条爆款，更多来自长期的稳定输出。更新频率稳定、语气自然、不过度营销，这三点看起来平淡，却是长期积累信任的基础。反之，忽冷忽热的更新节奏，往往会让观众摸不清边界。",
      },
      {
        type: "quote",
        text: "观众愿意持续关注的，从来不是一个完美的人，而是一个始终如一的人。",
      },
      {
        type: "p",
        text: "需要说明的是，我们对艺人日常的观察只停留在公开更新的层面，讨论的是内容策略与传播效果，不涉及任何私人生活。这也是我们做人物板块一直坚持的边界。",
      },
    ],
  },
  {
    id: "live-festival-observation",
    title: "音乐节现场观察：观众为什么愿意为一首歌站上八小时",
    categorySlug: "live",
    excerpt:
      "一场音乐节，观众平均要在场地里待上八个小时。从排队、占位到等待压轴，这八小时里究竟发生了什么？我们跟了一场演出，记下了现场的真实节奏。",
    cover:
      "https://readdy.ai/api/search-image?query=Wide%20open%20air%20music%20festival%20field%20at%20dusk%20with%20stage%20lights%20and%20a%20large%20crowd%20seen%20from%20behind%2C%20warm%20amber%20glow%20and%20dust%20in%20the%20air%2C%20cinematic%20documentary%20photography%2C%20high%20detail&width=1200&height=800&seq=hlk-cover-live-01&orientation=landscape",
    coverAlt: "户外音乐节黄昏现场的人群示意图",
    author: "编辑部 · 陆遥",
    authorRole: "现场板块编辑",
    date: "2026-09-08",
    readTime: "7 分钟",
    views: 21380,
    comments: 118,
    tags: ["音乐节", "现场观察", "演出"],
    hot: false,
    blocks: [
      {
        type: "p",
        text: "从下午三点到晚上十一点，一场音乐节的完整观看体验远超出一首歌的长度。真正让观众留下来的，往往不是压轴的那一刻，而是这八小时里不断累积的现场感。",
      },
      { type: "h2", text: "排队与占位：现场体验的前置成本" },
      {
        type: "p",
        text: "音乐节的第一个环节其实是排队。早到的人为了一个更靠前的位置，愿意在烈日下等上两三个小时。这段等待本身，也成了现场体验的一部分，人们在这里结识邻座，交换歌单，提前进入状态。",
      },
      { type: "h2", text: "八小时里，观众在等什么" },
      {
        type: "list",
        items: [
          "等自己熟悉的歌：对很多人来说，全场合唱的瞬间才是真正的目标。",
          "等一个氛围：即使不是最喜欢的乐队，现场灯光与人群的共振本身就值得体验。",
          "等一次释放：在现场大声合唱，是一种在日常生活里很难获得的情绪出口。",
        ],
      },
      {
        type: "quote",
        text: "观众买的从来不是一张门票，而是一个可以毫无顾忌大声唱歌的下午。",
      },
      { type: "h2", text: "给第一次去现场的人的三点提醒" },
      {
        type: "list",
        items: [
          "提前查好场地动线，避开最拥堵的入场时段。",
          "带好耳塞与补水，保护听力与体力比抢占位置更重要。",
          "不要执着于全程录像，现场感一旦抬手就会被耽误。",
        ],
      },
    ],
  },
  {
    id: "insight-attention-economy",
    title: "注意力经济学：为什么我们总在深夜刷到同一条热点",
    categorySlug: "insight",
    excerpt:
      "同一条热点在不同平台反复出现，并非巧合。推荐机制、发布节奏与我们的作息共同决定了什么内容会被看见。",
    cover:
      "https://readdy.ai/api/search-image?query=Minimal%20conceptual%20still%20life%20of%20a%20glowing%20smartphone%20screen%20on%20a%20dark%20wooden%20desk%20at%20night%20with%20warm%20amber%20lamp%20light%2C%20thoughtful%20editorial%20photography%2C%20high%20detail%2C%20shallow%20depth%20of%20field&width=1200&height=800&seq=hlk-cover-insight-01&orientation=landscape",
    coverAlt: "深夜桌面手机屏幕发光的示意图",
    author: "编辑部 · 陈默",
    authorRole: "深度栏目主笔",
    date: "2026-09-07",
    readTime: "9 分钟",
    views: 18920,
    comments: 96,
    tags: ["注意力经济", "推荐机制", "内容趋势"],
    hot: false,
    blocks: [
      {
        type: "p",
        text: "很多人都有类似的体验：晚上十一点打开手机，随手一刷，又是那条白天已经看过的热点。这并非错觉，也不是平台在循环推送，而是推荐机制、内容生产节奏与我们自身的作息共同作用的结果。",
      },
      { type: "h2", text: "推荐机制偏爱什么" },
      {
        type: "p",
        text: "推荐系统的核心目标是延长停留时间。它会更倾向于推送已经被验证过的内容，也就是那些完播率高、互动多的热点。当一个内容被判定为有效，平台就会在相似人群之间反复分发，直到边际效果下降。",
      },
      { type: "h2", text: "深夜为什么是热点的黄金时段" },
      {
        type: "list",
        items: [
          "用户空闲时间集中：多数人的整块空闲时间出现在晚间，此时在线人数最多。",
          "情绪更易被触动：疲惫状态下，人更容易对情绪化内容产生反应。",
          "内容方集中发布：很多创作者选择晚间发布，以争取更高的初始流量。",
        ],
      },
      {
        type: "quote",
        text: "我们以为自己在选择看什么，其实很多时候是在被时间选择。",
      },
      { type: "h2", text: "如何夺回自己的注意力" },
      {
        type: "p",
        text: "与其依赖算法的投喂，不如主动设定信息入口。固定时间、固定来源地获取经过筛选的内容，能显著降低被动刷信息带来的疲惫感。这也是我们坚持做每日看点清单的原因：把选择权还给读者，而不是让算法替你决定晚上看什么。",
      },
    ],
  },
  {
    id: "variety-new-format-review",
    title: "老牌综艺换新赛制：口碑回升背后的三个调整",
    categorySlug: "variety",
    excerpt:
      "一档播了多季的综艺更换赛制后口碑回升，观众讨论度也明显上升。我们对比了新老两季的差异，找到了三个关键调整。",
    cover:
      "https://readdy.ai/api/search-image?query=Modern%20television%20studio%20set%20with%20geometric%20warm%20amber%20lighting%20panels%20and%20empty%20seats%2C%20clean%20editorial%20entertainment%20photography%2C%20warm%20neutral%20palette%2C%20high%20detail%2C%20wide%20composition&width=1200&height=800&seq=hlk-cover-variety-02&orientation=landscape",
    coverAlt: "综艺演播厅灯光装置示意图",
    author: "编辑部 · 何一",
    authorRole: "综艺板块编辑",
    date: "2026-09-06",
    readTime: "6 分钟",
    views: 16740,
    comments: 87,
    tags: ["综艺赛制", "口碑", "节目观察"],
    hot: false,
    blocks: [
      {
        type: "p",
        text: "一档节目播到第五季之后，观众往往比节目组更清楚它的套路。当套路被看穿，口碑下滑几乎是必然。有意思的是，这一季更换赛制之后，讨论度明显回升，观众的评价也从「审美疲劳」转向「有点意思」。",
      },
      { type: "h2", text: "调整一：把规则讲清楚，而不是靠悬念硬撑" },
      {
        type: "p",
        text: "过去的做法是刻意模糊规则，靠剪辑制造悬念。新一季反过来，每期开头就把规则讲透。看似降低了悬念，实际上把观众的注意力从「猜规则」转移到「看博弈」上，节目反而更抓人。",
      },
      { type: "h2", text: "调整二：减少无效环节，把时长还给内容" },
      {
        type: "list",
        items: [
          "删掉了重复的自我介绍环节，嘉宾在第一轮就进入对抗。",
          "压缩了采访段落，把释放情绪的部分留在关键节点。",
          "增加了嘉宾之间的直接对话时间，让化学反应自然发生。",
        ],
      },
      { type: "h2", text: "调整三：给嘉宾留出发挥空间" },
      {
        type: "p",
        text: "最明显的改变，是节目不再把嘉宾的每一句话都写进流程。留白增多之后，临场反应变多，那些出圈的名场面也大多诞生于这些没有被安排的瞬间。",
      },
      {
        type: "quote",
        text: "好的赛制不是让节目更好看，而是让嘉宾更真实。",
      },
    ],
  },
  {
    id: "drama-ip-adaptation-traps",
    title: "IP 改编为何常常踩雷：原著粉真正在意的是什么",
    categorySlug: "drama",
    excerpt:
      "几乎每一部 IP 改编剧上线都会引发原著粉的讨论。与其说他们苛刻，不如说他们在意的点其实非常集中，而且往往不是剧情本身。",
    cover:
      "https://readdy.ai/api/search-image?query=Stack%20of%20old%20hardcover%20books%20and%20a%20single%20open%20script%20on%20a%20warm%20wooden%20table%2C%20soft%20window%20light%2C%20cozy%20editorial%20still%20life%20photography%2C%20amber%20and%20cream%20tones%2C%20high%20detail&width=1200&height=800&seq=hlk-cover-drama-02&orientation=landscape",
    coverAlt: "桌上书籍与剧本的示意图",
    author: "编辑部 · 沈迟",
    authorRole: "影视板块编辑",
    date: "2026-09-05",
    readTime: "7 分钟",
    views: 15230,
    comments: 132,
    tags: ["IP改编", "原著粉", "剧集观察"],
    hot: false,
    blocks: [
      {
        type: "p",
        text: "IP 改编常常在开播第一天就陷入争议。表面上看，原著粉的要求很多，但把讨论梳理一遍会发现，他们真正在意的点其实高度一致，而且大多与剧情改没改关系不大。",
      },
      { type: "h2", text: "原著粉在意的三个核心" },
      {
        type: "list",
        items: [
          "人物的内核有没有保住：情节可以改，但人物的动机与性格底色一旦偏移，观感立刻断裂。",
          "世界观的规则是否自洽：改编可以简化设定，但不能前后矛盾。",
          "有没有被尊重：观众能接受不完美，但很难接受创作者对原著的轻慢态度。",
        ],
      },
      { type: "h2", text: "改编本身没有错，问题在于取舍" },
      {
        type: "p",
        text: "小说和剧集是两种媒介，直接照搬反而容易出问题。真正成熟的做法是明确要保留什么、舍弃什么，并且让取舍服务于人物。可惜很多改编在取舍上摇摆，既想留住原著粉，又想吸引新观众，最后两头都没接住。",
      },
      {
        type: "quote",
        text: "改编的自由从来不是问题，问题的关键是取舍背后有没有一个清晰的判断。",
      },
      {
        type: "p",
        text: "对观众来说，与其纠结「像不像原著」，不如先判断这部剧有没有讲好自己的故事。能把人物立起来、把规则讲明白的改编，通常都不会太差。",
      },
    ],
  },
  {
    id: "internet-comment-section",
    title: "评论区正在成为第二内容场：高赞留言的四种类型",
    categorySlug: "internet",
    excerpt:
      "很多人看完视频的第一件事是翻评论区。当评论区本身成为内容场，高赞留言也开始有了规律可循。我们总结了其中最常见的四种类型。",
    cover:
      "https://readdy.ai/api/search-image?query=Close%20up%20abstract%20of%20warm%20amber%20chat%20bubbles%20floating%20over%20a%20dark%20charcoal%20backdrop%2C%20minimal%20digital%20communication%20concept%2C%20editorial%20technology%20photography%2C%20high%20detail%2C%20soft%20glow&width=1200&height=800&seq=hlk-cover-internet-02&orientation=landscape",
    coverAlt: "评论气泡概念的示意图",
    author: "编辑部 · 郑野",
    authorRole: "热点板块编辑",
    date: "2026-09-04",
    readTime: "5 分钟",
    views: 13470,
    comments: 108,
    tags: ["评论区", "高赞留言", "内容场"],
    hot: false,
    blocks: [
      {
        type: "p",
        text: "「我是来看评论的」这句话已经成了许多人的日常。当评论区从附属品变成独立的观看理由，高赞留言本身也具备了一定的内容价值。把热门评论区翻一遍，会发现高赞留言其实有迹可循。",
      },
      { type: "h2", text: "四种最常见的高赞类型" },
      {
        type: "list",
        items: [
          "补充型：提供了原内容没有的背景信息，让其他观众一次看明白。",
          "共鸣型：说出大家都想说但没说出口的那句话，转发率高。",
          "反转型：用一句话推翻前面的气氛，靠意外性获得点赞。",
          "总结型：把冗长的内容浓缩成一句判断，替观众节省了理解成本。",
        ],
      },
      { type: "h2", text: "为什么评论区值得被认真对待" },
      {
        type: "p",
        text: "评论区是观众真实反应的集合地，也是内容二次传播的重要一环。一条高赞留言有时能让一个原本平淡的内容重新获得关注，这本身就是一种内容再生产。",
      },
      {
        type: "quote",
        text: "当观众开始为评论而来，内容的生产者就不只是创作者，也包括每一个留言的人。",
      },
    ],
  },
  {
    id: "insight-short-vs-long-2026",
    title: "2026 文娱内容观察：短内容与长叙事的重新分工",
    categorySlug: "insight",
    excerpt:
      "短内容负责触达，长叙事负责沉淀，两者的关系正在从竞争转向配合。我们梳理了今年内容行业里几个值得注意的变化。",
    cover:
      "https://readdy.ai/api/search-image?query=Split%20conceptual%20composition%20of%20many%20small%20amber%20glowing%20panels%20on%20one%20side%20and%20a%20single%20large%20warm%20lit%20frame%20on%20the%20other%2C%20abstract%20editorial%20design%2C%20deep%20charcoal%20and%20gold%20palette%2C%20high%20detail&width=1200&height=800&seq=hlk-cover-insight-02&orientation=landscape",
    coverAlt: "短内容与长内容对比概念示意图",
    author: "编辑部 · 陈默",
    authorRole: "深度栏目主笔",
    date: "2026-09-03",
    readTime: "8 分钟",
    views: 11980,
    comments: 74,
    tags: ["内容趋势", "短内容", "长叙事"],
    hot: false,
    blocks: [
      {
        type: "p",
        text: "过去几年，短内容与长内容常被放在对立面讨论，仿佛一方崛起就意味着另一方衰退。但今年的情况有些不同：越来越多的团队开始把两者当作同一套流程的不同环节。",
      },
      { type: "h2", text: "短内容负责触达，长叙事负责沉淀" },
      {
        type: "p",
        text: "短内容擅长在几秒钟内完成触达，把观众拉进来；而真正让观众留下来、建立长期关系的，仍然是完整的长叙事。两者的关系不是替代，而是分工。",
      },
      { type: "h2", text: "今年值得注意的三个变化" },
      {
        type: "list",
        items: [
          "长内容的入口越来越依赖短内容：先被片段吸引，再去看完整内容，成为常见路径。",
          "短内容开始追求结构：不再只是高光堆叠，而是像完整的作品一样有起承转合。",
          "观众对「被投喂」越来越警惕：主动筛选信息的意识正在上升。",
        ],
      },
      {
        type: "quote",
        text: "内容行业真正的竞争，从来不是长短之争，而是能否让观众觉得自己的时间值得。",
      },
      {
        type: "p",
        text: "这也解释了为什么「精选」这件事变得越来越重要。当内容供给过剩，帮助读者完成筛选、节省时间的服务，本身就有了明确的价值。",
      },
    ],
  },
];

export const getArticleById = (id: string): Article | undefined =>
  articles.find((item) => item.id === id);

export const getArticlesByCategory = (slug: string): Article[] =>
  articles.filter((item) => item.categorySlug === slug);

export const searchArticles = (keyword: string): Article[] => {
  const q = keyword.trim().toLowerCase();
  if (!q) return articles;
  return articles.filter(
    (item) =>
      item.title.toLowerCase().includes(q) ||
      item.excerpt.toLowerCase().includes(q) ||
      item.tags.some((tag) => tag.toLowerCase().includes(q)),
  );
};

export const hotArticles = articles.filter((item) => item.hot);